import clsx from 'clsx';
import {
	createContext,
	useContext,
	useMemo,
	useRef,
	useState,
	type PropsWithChildren
} from 'react';

type ContextState = {
	isEnabled: boolean;
	currentDraggableId: string | null;
	setCurrentDraggableId: (id: string | null) => void;
};
const Context = createContext<ContextState | null>(null);

export const DragAndDropProvider: React.FC<PropsWithChildren<{ isEnabled: boolean }>> = ({
	children,
	isEnabled
}) => {
	const [currentDraggableId, setCurrentDraggableId] = useState<string | null>(null);
	const context = useMemo(
		() => ({
			currentDraggableId,
			setCurrentDraggableId,
			isEnabled
		}),
		[currentDraggableId, setCurrentDraggableId, isEnabled]
	);
	return <Context.Provider value={context}>{children}</Context.Provider>;
};

export const DroppableArea: React.FC<{
	className?: string;
	onDrop: (draggableId: string) => void;
	includeIds?: string[];
}> = ({ className, onDrop, includeIds }) => {
	const [isDraggingOver, setIsDraggingOver] = useState(false);

	const { currentDraggableId, isEnabled } = useDragContext();

	if (currentDraggableId && includeIds && !includeIds.includes(currentDraggableId)) {
		return <div className={className} />;
	}

	const handleDragEnter = () => {
		setIsDraggingOver(true);
	};
	const handleDragLeave = () => {
		setIsDraggingOver(false);
	};
	const handleDrop = () => {
		setIsDraggingOver(false);
		if (currentDraggableId) {
			onDrop(currentDraggableId);
		}
	};

	if (!isEnabled) {
		return <div className={className} />;
	}

	return (
		<div
			onDragEnter={handleDragEnter}
			onDragLeave={handleDragLeave}
			onDrop={handleDrop}
			onDragOver={e => {
				e.preventDefault();
				e.stopPropagation();
			}}
			className={clsx(
				className,
				'relative',
				isDraggingOver && 'bg-green-500 opacity-100',
				!isDraggingOver && 'opacity-0'
			)}
		>
			{currentDraggableId && <div className="absolute -inset-x-8 -inset-y-4" />}
		</div>
	);
};

export const DraggableElement: React.FC<
	PropsWithChildren<{
		className?: string;
		id: string;
	}>
> = ({ className, children, id }) => {
	const { currentDraggableId, setCurrentDraggableId, isEnabled } = useDragContext();
	const isDragging = currentDraggableId === id;
	const ref = useRef<HTMLDivElement>(null);

	return (
		<div
			draggable={isEnabled}
			className={clsx(
				className,
				isEnabled && 'cursor-move',
				isDragging && 'bg-gray-200 border-2 border-dashed border-gray-300 rounded-md'
			)}
			style={{
				transition: isDragging ? 'opacity 0.3s ease' : 'none',
				opacity: isDragging ? 0.5 : 1
			}}
			onDragStart={e => {
				setCurrentDraggableId(id);
				// Set custom drag image to show the actual element instead of URL
				if (ref.current) {
					e.dataTransfer.setDragImage(ref.current, 0, 0);
				}
			}}
			onDragEnd={() => {
				setCurrentDraggableId(null);
			}}
		>
			<div ref={ref}>{children}</div>
		</div>
	);
};

const useDragContext = () => {
	const context = useContext(Context);
	if (!context) {
		throw new Error('useDragContext must be used within a DragAndDropProvider');
	}
	return context;
};
